export interface TimeTranslations {
  noData: string;
  rightNow: string;
  min: string;
  hour: string;
  hours: string;
  day: string;
  days: string;
  month: string;
  months: string;
  and: string;
}

const defaultTimeTranslations: TimeTranslations = {
  noData: "Sin datos",
  rightNow: "Ahora mismo",
  min: "min",
  hour: "hora",
  hours: "horas",
  day: "día",
  days: "días",
  month: "mes",
  months: "meses",
  and: "y",
};

export const getTimeAgo = (seconds: number, translations?: TimeTranslations): string => {
  const t = translations || defaultTimeTranslations;
  
  // Manejar casos de timestamp inválido o 0 (sin datos)
  if (!seconds || seconds <= 0) return t.noData;

  // Validar que el timestamp sea razonable (después del año 2000)
  // El timestamp de Unix para el año 2000 es aproximadamente 946684800
  if (seconds < 946684800) return t.noData;

  const now = Date.now();
  const updatedTime = seconds * 1000;
  const diffMinutes = Math.floor((now - updatedTime) / 60000);

  // Permitir tolerancia de hasta 5 minutos en el futuro (desincronización de relojes)
  // Si está más de 5 minutos en el futuro o más de 5 años en el pasado, mostrar sin datos
  if (diffMinutes < -5 || diffMinutes > 2628000) return t.noData; // 2628000 min ≈ 5 años

  // Si está en el futuro cercano o hace menos de 1 minuto, mostrar "Ahora mismo"
  if (diffMinutes < 1) return t.rightNow;

  const totalDays = Math.floor(diffMinutes / 1440);
  const hoursVal = Math.floor((diffMinutes % 1440) / 60);
  const minutesVal = diffMinutes % 60;
  const monthsVal = Math.floor(totalDays / 30);
  const daysVal = totalDays % 30;

  // Más de 1 mes
  if (monthsVal >= 1) {
    const monthsText = monthsVal === 1 ? `1 ${t.month}` : `${monthsVal} ${t.months}`;
    if (daysVal === 0) return monthsText;
    const daysText = daysVal === 1 ? `1 ${t.day}` : `${daysVal} ${t.days}`;
    return `${monthsText} ${t.and} ${daysText}`;
  }

  // Solo minutos
  if (totalDays === 0 && hoursVal === 0) {
    return `${minutesVal} ${t.min}`;
  }

  // Solo horas (con o sin minutos)
  if (totalDays === 0) {
    const hoursText = hoursVal === 1 ? `1 ${t.hour}` : `${hoursVal} ${t.hours}`;
    return minutesVal === 0 ? hoursText : `${hoursText} ${t.and} ${minutesVal} ${t.min}`;
  }

  // Días (con horas y/o minutos)
  const daysText = totalDays === 1 ? `1 ${t.day}` : `${totalDays} ${t.days}`;

  if (hoursVal === 0 && minutesVal === 0) return daysText;
  if (hoursVal === 0) return `${daysText} ${t.and} ${minutesVal} ${t.min}`;
  if (minutesVal === 0) {
    const hoursText = hoursVal === 1 ? `1 ${t.hour}` : `${hoursVal} ${t.hours}`;
    return `${daysText} ${t.and} ${hoursText}`;
  }

  const hoursText = hoursVal === 1 ? `1 ${t.hour}` : `${hoursVal} ${t.hours}`;
  return `${daysText}, ${hoursText} ${t.and} ${minutesVal} ${t.min}`;
};

export const formatDate = (seconds: number): string => {
  const date = new Date(seconds * 1000);

  const meses = [
    'ene', 'feb', 'mar', 'abr', 'may', 'jun',
    'jul', 'ago', 'sep', 'oct', 'nov', 'dic'
  ];

  const dia = date.getDate();
  const mes = meses[date.getMonth()];
  const año = date.getFullYear();

  let horas = date.getHours();
  const minutos = date.getMinutes().toString().padStart(2, '0');
  const periodo = horas >= 12 ? 'p.m.' : 'a.m.';

  horas = horas % 12 || 12;

  return `${dia} ${mes} ${año}, ${horas}:${minutos} ${periodo}`;
};

/**
 * Formatea una fecha que puede ser:
 * - Timestamp de Firestore ({ _seconds: number })
 * - String ISO (ej: "2024-01-15T16:00:00.000Z")
 * - Date object
 * La fecha se muestra en la zona horaria LOCAL del usuario
 */
export const formatScheduleDate = (
  value: string | { _seconds: number } | Date | null | undefined
): string => {
  if (!value) return "-";

  let date: Date;

  if (typeof value === "object" && "_seconds" in value) {
    // Timestamp de Firestore
    date = new Date(value._seconds * 1000);
  } else if (typeof value === "string") {
    // String ISO o cualquier formato de fecha
    date = new Date(value);
  } else if (value instanceof Date) {
    date = value;
  } else {
    return "-";
  }

  if (isNaN(date.getTime())) return "-";

  const meses = [
    'ene', 'feb', 'mar', 'abr', 'may', 'jun',
    'jul', 'ago', 'sep', 'oct', 'nov', 'dic'
  ];

  const dia = date.getDate();
  const mes = meses[date.getMonth()];
  const año = date.getFullYear();

  let horas = date.getHours();
  const minutos = date.getMinutes().toString().padStart(2, '0');
  const periodo = horas >= 12 ? 'p.m.' : 'a.m.';

  horas = horas % 12 || 12;

  return `${dia} ${mes} ${año}, ${horas}:${minutos} ${periodo}`;
};


export const getDuration = (createdAt: any, endedAt: any): string => {
  if (!createdAt) return "-";

  const start = createdAt._seconds ? createdAt._seconds * 1000 : new Date(createdAt).getTime();
  const end = endedAt
    ? endedAt._seconds
      ? endedAt._seconds * 1000
      : new Date(endedAt).getTime()
    : Date.now();

  const diffMs = end - start;
  const hours = Math.floor(diffMs / (1000 * 60 * 60));
  const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));

  return `${hours}h ${minutes}m`;
};

export const calculateDuration = (createdAt: any, endedAt: any): string => {
  if (!createdAt) return "-";

  const start = createdAt._seconds ? createdAt._seconds * 1000 : new Date(createdAt).getTime();
  const end = endedAt
    ? endedAt._seconds
      ? endedAt._seconds * 1000
      : new Date(endedAt).getTime()
    : Date.now();

  const diffMs = end - start;
  const hours = Math.floor(diffMs / (1000 * 60 * 60));
  const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));

  return `${hours}h ${minutes}m`;
};